// src/store.js
import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { updateEvent } from "store/CalendarSlice";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";
import _ from "lodash";
import moment from "moment";

// Default query param param values.
const defaultParams = { client_id: null, sent: "no", status: "" };

const loadInvoiceParams = () => {
  const storedInvoiceParams = localStorage.getItem("queryInvoiceParams");
  const params = storedInvoiceParams ? JSON.parse(storedInvoiceParams) : {};
  return { ...defaultParams, ...params };
};

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  invoices: [],
  report: null,
  invoice: null,
  invoiceEvents: [],
  loading: false,
  stats: {},
  error: null,
  exportInfo: {},
  params: loadInvoiceParams(),
  // Loading state
  invoicesLoaded: false,
  // Validation state
  validation: null,
  validationLoading: false,
  validationError: null,
};

// ------------------------------------------------------------------------
// Async Thunks

export const selectInvoicesLoaded = (state) =>
  state.invoiceReducer.invoicesLoaded;
export const selectInvoices = (state) => state.invoiceReducer.invoices;

// FOR REVIEW
export const getInvoiceStats = createAsyncThunk(
  "data/getInvoiceStats",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/invoices/stats");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

let lastRefresh = 0;

// For Review
export const calculateInvoice = createAsyncThunk(
  "data/reCalcInvoice",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/invoices/${id}/calculate`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// FOR REVIEW
export const exportInvoiceEvents = createAsyncThunk(
  "data/exportInvoiceEvents",
  async ({ id, params = { format: "csv" } }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/invoices/${id}/export`,
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchExportFormats = createAsyncThunk(
  "data/fetchExportFormats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/invoices/export/formats");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch export formats"
      );
    }
  }
);

// FOR REVIEW - does this work as expected compared to the axios/API version?
export const getSupabaseInvoiceStats = createAsyncThunk(
  "data/getSupabaseInvoiceStats",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { data, error } = await supabase.from("invoices").select("*");

      if (error) {
        throw error;
      }

      // Calculate stats from the data
      const openInvoices = data.filter((invoice) => invoice.status === "open");
      const closedInvoices = data.filter((invoice) => invoice.status === "closed");
      const pendingInvoices = data.filter((invoice) => invoice.status === "pending");
      const paidInvoices = data.filter((invoice) => invoice.status === "paid");
      
      const stats = {
        total: data.length,
        sent: data.filter((invoice) => invoice.sent).length,
        draft: data.filter((invoice) => !invoice.sent).length,
        paid: paidInvoices.length,
        pending: data.filter((invoice) => invoice.status === "pending").length,
        overdue: data.filter((invoice) => invoice.status === "overdue").length,
        open: openInvoices.length,
        closed: closedInvoices.length,
        openAmount: openInvoices.reduce((sum, inv) => sum + (inv.totals?.amount || 0), 0),
        closedAmount: closedInvoices.reduce((sum, inv) => sum + (inv.totals?.amount || 0), 0),
        pendingAmount: pendingInvoices.reduce((sum, inv) => sum + (inv.totals?.amount || 0), 0),
        paidAmount: paidInvoices.reduce((sum, inv) => sum + (inv.totals?.amount || 0), 0),
      };

      return stats;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSupabaseInvoices = createAsyncThunk(
  "data/fetchSupabaseInvoices",
  async ({ userId, ...params } = {}, { getState, rejectWithValue }) => {
    try {
      // console.log("fetchSupabaseInvoices: Starting fetch...");

      if (lastRefresh > 0 && moment().unix() - lastRefresh < 3) {
        lastRefresh = 0;
        params.force = "yes";
      }

      if (!userId) {
        // console.error("fetchSupabaseInvoices: User not authenticated");
        throw new Error("User not authenticated");
      }

      // console.log("fetchSupabaseInvoices: User authenticated", userId);

      // Build Supabase query
      let query = supabase.from("invoices").select("*").eq("uid", userId);

      // Apply filters
      // Check for client_id filter (mapped from clientId in mapParamsForAPI)
      if (params.client_id && params.client_id !== "" && params.client_id !== null) {
        // Ensure client_id is a string (UUIDs should be strings)
        const clientId = typeof params.client_id === 'string' ? params.client_id : String(params.client_id);
        console.log("Filtering by client_id:", clientId, "type:", typeof clientId);
        console.log("Query before client_id filter");
        query = query.eq("client_id", clientId);
        console.log("Query after client_id filter applied");
      } else {
        console.log("No client_id filter applied. params.client_id:", params.client_id, "type:", typeof params.client_id);
      }
      // Only filter by sent if explicitly set to "yes" or "no" (not empty string)
      // Database stores sent as string "yes"/"no" (based on API code)
      if (params.sent && params.sent !== "") {
        console.log("Filtering by sent:", params.sent, "type:", typeof params.sent);
        console.log("Query before sent filter:", query);
        // Filter by string value directly
        query = query.eq("sent", params.sent);
        console.log("Query after sent filter applied");
      } else {
        console.log("No sent filter applied (empty or not set), params.sent:", params.sent);
      }
      if (params.status) {
        query = query.eq("status", params.status);
      }
      
      console.log("fetchSupabaseInvoices: Query params:", { client_id: params.client_id, sent: params.sent, status: params.status });
      console.log("fetchSupabaseInvoices: Full params object:", params);
      console.log("fetchSupabaseInvoices: All param keys:", Object.keys(params));

      // Order by created_at desc
      query = query.order("created_at", { ascending: false });

      console.log("fetchSupabaseInvoices: About to execute query with filters:", {
        client_id: params.client_id,
        sent: params.sent,
        status: params.status
      });
      const { data, error } = await query;
      
      if (error) {
        console.error("fetchSupabaseInvoices: Query error:", error);
        console.error("fetchSupabaseInvoices: Error details:", JSON.stringify(error, null, 2));
        throw error;
      }

      console.log(
        "fetchSupabaseInvoices: Query successful, returned",
        data?.length || 0,
        "invoices"
      );
      
      // Debug: Check what values we got back
      if (data && data.length > 0) {
        const sentValues = [...new Set(data.map(inv => `${inv.id}: sent=${JSON.stringify(inv.sent)} (${typeof inv.sent})`))];
        console.log("Sample sent values from results:", sentValues.slice(0, 5));
        
        // Debug: Check client_id values in results
        const clientIds = [...new Set(data.map(inv => `${inv.id}: client_id=${inv.client_id} (${typeof inv.client_id})`))];
        console.log("Sample client_id values from results:", clientIds.slice(0, 5));
        console.log("Total invoices returned:", data.length);
        if (params.client_id) {
          const matchingClientIds = data.filter(inv => inv.client_id === params.client_id || String(inv.client_id) === String(params.client_id));
          console.log("Invoices matching client_id filter:", matchingClientIds.length, "out of", data.length);
        }
      }
      lastRefresh = moment().unix();
      return data;
    } catch (error) {
      console.error("fetchSupabaseInvoices: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOneSupabaseInvoice = createAsyncThunk(
  "data/fetchOneSupabaseInvoice",
  async ({ id, userId }, { getState, rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          *,
          client:clients!invoices_client_id_fkey ( name, project )
        `
        )
        .eq("id", id)
        .eq("uid", userId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error.code === "PGRST116") {
        return rejectWithValue({
          status: 404,
          message: "Invoice not found",
        });
      }
      return rejectWithValue({
        status: 500,
        message: error.message || "Network error",
      });
    }
  }
);

// New thunk for fetching invoice without authentication (public view)
export const fetchPublicSupabaseInvoice = createAsyncThunk(
  "data/fetchPublicSupabaseInvoice",
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("invoices")
        .select("client:clients!invoices_client_id_fkey(name)")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      if (error.code === "PGRST116") {
        return rejectWithValue({
          status: 404,
          message: "Invoice not found",
        });
      }
      return rejectWithValue({
        status: 500,
        message: error.message || "Network error",
      });
    }
  }
);

// Helper function to get the next invoice number for a user
export const getNextInvoiceNumber = createAsyncThunk(
  "data/getNextInvoiceNumber",
  async ({ userId }, { rejectWithValue }) => {
    try {
      // Use RPC function to get max invoice number in a single SQL command
      const { data, error } = await supabase.rpc('get_max_invoice_number', {
        user_id: userId
      });

      if (error) {
        console.error("getNextInvoiceNumber: RPC error:", error);
        throw error;
      }

      console.log("getNextInvoiceNumber: Max invoice number from DB:", data, "Type:", typeof data);

      // Return next number (data will be the max number or null)
      const nextNumber = (data || 0) + 1;
      console.log("getNextInvoiceNumber: Calculated next number:", nextNumber);
      return nextNumber;
    } catch (error) {
      console.error("getNextInvoiceNumber: Error getting next invoice number:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const createSupabaseInvoice = createAsyncThunk(
  "data/createSupabaseInvoice",
  async ({ userId, ...data }, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log("createSupabaseInvoice: Starting creation with data:", data);

      if (!userId) {
        console.error("createSupabaseInvoice: User not authenticated");
        throw new Error("User not authenticated");
      }

      console.log("createSupabaseInvoice: User authenticated", userId);

      // Validate required fields
      if (!data.client_id) {
        throw new Error("Client ID is required");
      }
      if (!data.from_date) {
        throw new Error("From date is required");
      }
      if (!data.to_date) {
        throw new Error("To date is required");
      }

      // Auto-generate invoice number if not provided
      let invoiceNum = data.invoice_num;
      
      console.log("createSupabaseInvoice: Initial invoice_num value:", invoiceNum, "Type:", typeof invoiceNum);
      
      // Convert string to integer if provided as string, or null if empty
      if (invoiceNum === "" || invoiceNum === null || invoiceNum === undefined || invoiceNum === "null") {
        // Auto-generate next number
        console.log("createSupabaseInvoice: Auto-generating invoice number...");
        const nextNumberResult = await dispatch(getNextInvoiceNumber({ userId }));
        console.log("createSupabaseInvoice: getNextInvoiceNumber result:", nextNumberResult);
        
        if (nextNumberResult.type === "data/getNextInvoiceNumber/fulfilled") {
          invoiceNum = nextNumberResult.payload; // Already an integer
          console.log("createSupabaseInvoice: Generated invoice number:", invoiceNum, "Type:", typeof invoiceNum);
        } else {
          // Fallback: start at 1 if we can't determine next number
          console.warn("createSupabaseInvoice: Failed to get next number, defaulting to 1. Error:", nextNumberResult.error);
          invoiceNum = 1;
        }
      } else {
        // Convert to integer if it's a string
        invoiceNum = typeof invoiceNum === 'string' ? parseInt(invoiceNum, 10) : invoiceNum;
        if (isNaN(invoiceNum)) {
          return rejectWithValue({ message: "Invoice number must be a valid number" });
        }
        console.log("createSupabaseInvoice: Using provided invoice number:", invoiceNum);
      }

      // Format dates and ensure proper data types
      const invoiceData = {
        ...data,
        invoice_num: invoiceNum, // Ensure it's an integer
        client_id: data.client_id.toString(), // Ensure client_id is string as per schema
        from_date: moment(data.from_date).format("YYYY-MM-DD"),
        to_date: moment(data.to_date).format("YYYY-MM-DD"),
        sent: data.sent, // Convert string to boolean
        uid: userId, // Explicitly set uid for RLS policy
        created_at: moment().toISOString(),
        updated_at: moment().toISOString(),
      };

      // Remove any empty or undefined fields that might cause UUID errors
      // But keep invoice_num even if it was 0 (which is falsy but valid)
      Object.keys(invoiceData).forEach((key) => {
        if (key === 'invoice_num') {
          // Keep invoice_num - ensure it's an integer (column is bigint)
          const numValue = typeof invoiceData[key] === 'number' ? invoiceData[key] : parseInt(invoiceData[key], 10);
          if (!isNaN(numValue)) {
            invoiceData[key] = numValue; // Keep as number for bigint column
          } else {
            // If invalid, remove it so it gets auto-generated
            delete invoiceData[key];
          }
          return;
        }
        if (invoiceData[key] === "" || invoiceData[key] === undefined) {
          delete invoiceData[key];
        }
      });

      console.log("createSupabaseInvoice: Prepared invoice data:", invoiceData);
      console.log("createSupabaseInvoice: invoice_num being saved:", invoiceData.invoice_num, "Type:", typeof invoiceData.invoice_num);

      console.log(
        "createSupabaseInvoice: Attempting to insert data:",
        invoiceData
      );

      const { data: result, error } = await supabase
        .from("invoices")
        .insert(invoiceData)
        .select()
        .single();

      if (error) {
        console.error("createSupabaseInvoice: Supabase error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        
        // Check if it's a unique constraint violation
        if (error.code === '23505' || error.message?.includes('unique')) {
          return rejectWithValue({
            message: `Invoice number ${invoiceNum} already exists. Please choose a different number.`,
            code: 'DUPLICATE_INVOICE_NUMBER'
          });
        }
        // Check if it's a type error (non-numeric value)
        if (error.code === '22P02' || error.message?.includes('invalid input syntax')) {
          return rejectWithValue({
            message: "Invoice number must be a valid number.",
            code: 'INVALID_INVOICE_NUMBER'
          });
        }
        
        throw error;
      }

      console.log(
        "createSupabaseInvoice: Invoice created successfully:",
        result
      );

      // Create log record for the new invoice (matching API behavior)
      if (result?.name) {
        try {
          const { error: logError } = await supabase
            .from("logs")
            .insert({
              uid: userId,
              description: `Created a new invoice "${result.name}".`,
              category: "invoice",
              calendar_id: null,
              data: null,
              status: "unread",
            });

          if (logError) {
            console.error("createSupabaseInvoice: Error creating log record:", logError);
            // Don't throw - log creation failure shouldn't fail invoice creation
          }
        } catch (logError) {
          console.error("createSupabaseInvoice: Exception creating log record:", logError);
          // Don't throw - log creation failure shouldn't fail invoice creation
        }
      }

      return result;
    } catch (error) {
      console.error("createSupabaseInvoice: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSupabaseInvoice = createAsyncThunk(
  "data/updateSupabaseInvoice",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const updateData = {
        ...data,
        updated_at: moment().toISOString(),
      };

      const { data: result, error } = await supabase
        .from("invoices")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// FOR REVIEW - does this work as expected compared to the axios/API version?
export const calculateSupabaseInvoice = createAsyncThunk(
  "data/calculateSupabaseInvoice",
  async ({ id }, { rejectWithValue }) => {
    try {
      // This would typically involve fetching related events and recalculating totals
      // For now, we'll just update the updated_at timestamp
      const { data, error } = await supabase
        .from("invoices")
        .update({
          updated_at: moment().toISOString(),
          calculated_at: moment().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// FOR REVIEW - does this work as expected compared to the axios/API version?
export const exportSupabaseInvoiceEvents = createAsyncThunk(
  "data/exportSupabaseInvoiceEvents",
  async ({ id, params = { format: "csv" } }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/invoices/${id}/export`,
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSupabaseInvoice = createAsyncThunk(
  "data/deleteSupabaseInvoice",
  async (id, { rejectWithValue, getState }) => {
    try {
      // Get invoice name for logging (from state or fetch)
      let invoiceName = null;
      const state = getState();
      const invoice = state.invoiceReducer.invoices?.find((inv) => inv.id === id);
      
      if (invoice) {
        invoiceName = invoice.name;
      } else {
        // If not in state, fetch it
        const { data: invoiceData } = await supabase
          .from("invoices")
          .select("name")
          .eq("id", id)
          .single();
        
        if (invoiceData) {
          invoiceName = invoiceData.name;
        }
      }

      // Get current user ID
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      const { error } = await supabase.from("invoices").delete().eq("id", id);

      if (error) {
        throw error;
      }

      // Create log record for the deleted invoice
      if (userId && invoiceName) {
        try {
          await supabase.from("logs").insert({
            uid: userId,
            description: `Removed an existing invoice "${invoiceName}".`,
            category: "invoice",
            calendar_id: null,
            data: null,
            status: "unread",
          });
        } catch (logError) {
          console.error("deleteSupabaseInvoice: Error creating log record:", logError);
          // Don't throw - log creation failure shouldn't fail invoice deletion
        }
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// FOR REVIEW - does this work as expected compared to the axios/API version?
export const fetchSupabaseInvoiceRecords = createAsyncThunk(
  "data/fetchSupabaseInvoiceRecords",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/events`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Fetch invoice events for the events view
export const fetchSupabaseInvoiceEvents = createAsyncThunk(
  "data/fetchSupabaseInvoiceEvents",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/events`);
      console.log("fetchSupabaseInvoiceEvents: Response:", response);
      return response.data.events;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const manageInvoice = createAsyncThunk(
  "data/manageInvoice",
  async ({ id, action, sendEmail, emails }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/invoices/${id}/manage`, {
        action,
        sendEmail,
        emails,
      });
      // Return structured response for client-side processing
      if (response.data && typeof response.data === "object") {
        return response.data;
      }
      return { success: true, error: null };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      return { success: false, error: errorMessage };
    }
  }
);

export const resendInvoice = createAsyncThunk(
  "data/resendInvoice",
  async ({ id, emails }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/resend`, {
        emails: Array.isArray(emails) ? emails : [emails],
      });
      // Return structured response for client-side processing
      if (response.data && typeof response.data === "object") {
        return response.data;
      }
      return { success: true, error: null };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      return { success: false, error: errorMessage };
    }
  }
);

export const exportAllInvoices = createAsyncThunk(
  "data/exportAllInvoices",
  async ({ format }, { rejectWithValue }) => {
    try {
      console.log("exportAllInvoices: Initiating export with format", format);

      const response = await axiosInstance.post("/invoices/dump", {
        format: format,
      });

      console.log("exportAllInvoices: Export initiated successfully");
      return response.data;
    } catch (error) {
      console.error("exportAllInvoices: Error initiating export", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to initiate export"
      );
    }
  }
);

export const rebuildInvoicePDF = createAsyncThunk(
  "data/rebuildInvoicePDF",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/rerender`);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateInvoiceProcessing = createAsyncThunk(
  "data/updateInvoiceProcessing",
  async ({ id, invoice_processing }, { rejectWithValue }) => {
    try {
      const { data: result, error } = await supabase
        .from("invoices")
        .update({ invoice_processing })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const validateInvoice = createAsyncThunk(
  "data/validateInvoice",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/validate`);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ------------------------------------------------------------------------
// Slices

export const InvoiceSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setInvoiceParam: (state, action) => {
      state.params[action.payload.key] = action.payload.value;
      localStorage.setItem("queryInvoiceParams", JSON.stringify(state.params));
    },
    // Realtime subscription actions
    addInvoice: (state, action) => {
      // Check if invoice already exists (prevent duplicates from realtime events)
      const existingIndex = state.invoices.findIndex(
        (inv) => inv.id === action.payload.id
      );
      if (existingIndex === -1) {
        // Add to beginning of array (newest first, matching fetch order)
        state.invoices.unshift(action.payload);
      }
    },
    updateInvoice: (state, action) => {
      if (!action.payload || !action.payload.id) {
        console.warn('updateInvoice: Invalid payload, missing id', action.payload);
        return;
      }

      const invoiceId = action.payload.id;
      const index = state.invoices.findIndex(
        (invoice) => invoice.id === invoiceId
      );
      
      if (index !== -1) {
        // Update existing invoice in the list
        state.invoices[index] = { ...state.invoices[index], ...action.payload };
        console.log('updateInvoice: Updated invoice in list:', invoiceId);
      } else {
        // Invoice not in list, add it (could be from realtime update)
        console.log('updateInvoice: Invoice not in list, adding:', invoiceId);
        state.invoices.unshift(action.payload);
      }
      
      // Also update state.invoice if it's the current invoice being viewed
      if (state.invoice && state.invoice.id === invoiceId) {
        state.invoice = { ...state.invoice, ...action.payload };
        console.log('updateInvoice: Updated current invoice being viewed:', invoiceId);
      }
    },
    removeInvoice: (state, action) => {
      // Remove from invoices array
      state.invoices = state.invoices.filter(
        (invoice) => invoice.id !== action.payload.id
      );
      
      // Clear state.invoice if it's the one being deleted
      if (state.invoice && state.invoice.id === action.payload.id) {
        state.invoice = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInvoiceStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(exportInvoiceEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.exportInfo = action.payload;
        state.invoicesLoaded = true;
      })
      // ------------------------------------------------------------
      .addCase(calculateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (rec) => rec.id === action.payload.id
        );
        if (index !== -1) {
          state.invoices[index] = Object.assign(
            {},
            state.invoices[index],
            action.payload
          );
        }
        // Also update state.invoice if it's the current invoice being viewed
        if (state.invoice && state.invoice.id === action.payload.id) {
          state.invoice = Object.assign({}, state.invoice, action.payload);
        }
      })
      // ------------------------------------------------------------
      // .addCase(fetchInvoiceRecords.pending, (state) => {
      //   state.loading = true;
      // })
      // .addCase(fetchInvoiceRecords.fulfilled, (state, action) => {
      //   state.loading = false;
      //   // state.invoices = state.invoices.filter(
      //   //   (rec) => rec.id !== action.payload
      //   // );
      //   // state.invoicesLoaded = true;
      // })
      // .addCase(fetchInvoiceRecords.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload;
      // })
      // ------------------------------------------------------------
      // Supabase versions
      .addCase(getSupabaseInvoiceStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(fetchSupabaseInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupabaseInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
        state.invoicesLoaded = true;
      })
      .addCase(fetchSupabaseInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(exportSupabaseInvoiceEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.exportInfo = action.payload;
        state.invoicesLoaded = true;
      })
      // ------------------------------------------------------------
      // Create Supabase
      .addCase(createSupabaseInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSupabaseInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.push(action.payload);
        state.invoicesLoaded = true;
      })
      .addCase(createSupabaseInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      // Delete Supabase
      .addCase(deleteSupabaseInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSupabaseInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.filter(
          (rec) => rec.id !== action.payload
        );
        state.invoicesLoaded = true;
      })
      .addCase(deleteSupabaseInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(updateSupabaseInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSupabaseInvoice.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(
          (rec) => rec.id === action.payload.id
        );
        if (index !== -1) {
          state.invoices[index] = Object.assign(
            {},
            state.invoices[index],
            action.payload
          );
        }
        state.loading = false;
      })
      .addCase(updateSupabaseInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(fetchOneSupabaseInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOneSupabaseInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload;
      })
      .addCase(fetchOneSupabaseInvoice.rejected, (state, action) => {
        state.loading = false;
        state.invoice = null;
        state.error = action.error.message;
      })
      // ------------------------------------------------------------
      .addCase(fetchPublicSupabaseInvoice.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPublicSupabaseInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload;
      })
      .addCase(fetchPublicSupabaseInvoice.rejected, (state, action) => {
        state.loading = false;
        state.invoice = null;
        state.error = action.error.message;
      })
      // ------------------------------------------------------------
      .addCase(calculateSupabaseInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (rec) => rec.id === action.payload.id
        );
        if (index !== -1) {
          state.invoices[index] = Object.assign(
            {},
            state.invoices[index],
            action.payload
          );
        }
      })
      // ------------------------------------------------------------
      .addCase(fetchSupabaseInvoiceRecords.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupabaseInvoiceRecords.fulfilled, (state, action) => {
        state.loading = false;
        // state.invoices = state.invoices.filter(
        //   (rec) => rec.id !== action.payload
        // );
        // state.invoicesLoaded = true;
      })
      .addCase(fetchSupabaseInvoiceRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(fetchSupabaseInvoiceEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupabaseInvoiceEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceEvents = action.payload;
      })
      .addCase(fetchSupabaseInvoiceEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(updateInvoiceProcessing.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateInvoiceProcessing.fulfilled, (state, action) => {
        state.loading = false;
        // Update invoice in state if it exists
        if (action.payload && action.payload.id) {
          const invoiceId = action.payload.id;
          const index = state.invoices.findIndex(
            (invoice) => invoice.id === invoiceId
          );
          if (index !== -1) {
            state.invoices[index] = { ...state.invoices[index], ...action.payload };
          }
          // Also update state.invoice if it's the current invoice being viewed
          if (state.invoice && state.invoice.id === invoiceId) {
            state.invoice = { ...state.invoice, ...action.payload };
          }
        }
      })
      .addCase(updateInvoiceProcessing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(exportAllInvoices.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportAllInvoices.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(validateInvoice.pending, (state) => {
        state.validationLoading = true;
        state.validationError = null;
      })
      .addCase(validateInvoice.fulfilled, (state, action) => {
        state.validationLoading = false;
        state.validation = action.payload;
        state.validationError = null;
      })
      .addCase(validateInvoice.rejected, (state, action) => {
        state.validationLoading = false;
        state.validationError = action.payload;
        state.validation = null;
      })
      // Merge calendar updates into the engagement events list (avoid full refetch).
      .addCase(updateEvent.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload?.id) return;
        const idx = state.invoiceEvents.findIndex((e) => e.id === payload.id);
        if (idx === -1) return;
        state.invoiceEvents[idx] = {
          ...state.invoiceEvents[idx],
          ...payload,
        };
      });
  },
});

// Export actions and reducers
export const { 
  setLoading, 
  setError, 
  setInvoiceParam,
  addInvoice,
  updateInvoice,
  removeInvoice
} = InvoiceSlice.actions;

export default InvoiceSlice.reducer;
